import React, { useContext, useEffect, useState } from "react";
import 'firebase/compat/firestore';
import auth from '../auth'
import firestore from '../firestore'
import Logo from "../components/Logo";
import { Button } from "../shadcn/Button";
import { GlobalContext } from "../context/GlobalContext";
import Input from "../components/Input";
import { AlertCircle, Lock, Mail } from "lucide-react";
import { GetDev2FANumbers } from "../hooks/firebase";
import Notice from "../components/Notice";
import TwoFactorAuth from "../components/2FA";
import { PendingGoogleAuth, useGoogleAuth, verifyPortalEmail } from "../hooks/googleAuth";
import { consumeImpersonationLoginMessage } from "../utils/impersonation";

const LoginForm = () => {
  const { authenticated, shouldLogin } = useContext(GlobalContext);

  const enable_2fa = !window.location.hostname.includes('localhost') && !window.location.hostname.includes('5.231.45.74') && !window.location.hostname.includes('vercel') && !window.location.hostname.includes('admin.krece.app');
  // const enable_2fa = true;
  const dev_2fa_numbers = GetDev2FANumbers(enable_2fa);
  const [twoFactorSuccess, setTwoFactorSuccess] = useState(false);
  const { startGoogleSignIn, completeGoogleLink, completeGooglePasswordCreation } = useGoogleAuth();

  const [loginData, setLoginData] = useState({
    storeEmail: '',
    password: '',
    password2: ''
  });
  const [loginError, setLoginError] = useState('');
  const [infoMessage, setInfoMessage] = useState('');
  const [page, setPage] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isGoogleLoading, setIsGoogleLoading] = useState(false);
  const [firebaseDown, setFirebaseDown] = useState(false);
  const [pendingGoogleAuth, setPendingGoogleAuth] = useState<PendingGoogleAuth | null>(null);

  useEffect(() => {
    if (authenticated || !shouldLogin) {
      setIsLoading(true);
    }
  }, [authenticated, shouldLogin]);

  useEffect(() => {
    const message = consumeImpersonationLoginMessage();
    if (message) setLoginError(message);
  }, []);

  const handleLoginInputChange = (e: any) => {
    setLoginData({ ...loginData, [e.target.name]: e.target.value });
  };

  const setPendingGoogleFlow = (pending: PendingGoogleAuth) => {
    setPendingGoogleAuth(pending);
    setLoginData({ storeEmail: pending.email, password: '', password2: '' });
    setLoginError('');
    setPage(pending.action === 'create_password' ? 3 : 1);
    setInfoMessage(
      pending.action === 'create_password'
        ? 'Crea una contraseña para habilitar Google y contraseña en esta cuenta.'
        : 'Ingresa tu contraseña una vez para conectar Google a tu cuenta.'
    );
  };

  const completePendingGoogleAuth = async () => {
    if (!pendingGoogleAuth) return false;

    if (!loginData.password || (pendingGoogleAuth.action === 'create_password' && !loginData.password2)) {
      setLoginError('Por favor, rellene todas las casillas.');
      return true;
    }
    if (pendingGoogleAuth.action === 'create_password' && loginData.password !== loginData.password2) {
      setLoginError('Las contraseñas no coinciden.');
      return true;
    }

    setLoginError('');
    setIsLoading(true);

    const result = pendingGoogleAuth.action === 'create_password'
      ? await completeGooglePasswordCreation(pendingGoogleAuth, loginData.password)
      : await completeGoogleLink(pendingGoogleAuth, loginData.password);

    if (result.success) return true;
    if ('pending' in result) {
      setPendingGoogleFlow(result.pending);
    } else {
      if (result.firebaseDown) setFirebaseDown(true);
      setLoginError(result.error);
    }
    setIsLoading(false);
    return true;
  };

  const handleSignUp = async () => {
    if (await completePendingGoogleAuth()) return;
    try {
      if (!loginData.storeEmail || !loginData.password || !loginData.password2) {
        setLoginError('Por favor, rellene todas las casillas.');
        return;
      } else if (loginData.password !== loginData.password2) {
        setLoginError('Las contraseñas no coinciden.');
        return;
      }

      setLoginError('')
      setIsLoading(true)

      firestore.collection('Stores').doc(loginData.storeEmail.toLowerCase()).get()
        .then((docSnapshot) => {
          if (docSnapshot.exists) {
            auth.createUserWithEmailAndPassword(loginData.storeEmail.toLowerCase(), loginData.password)
              .then(() => verifyPortalEmail(loginData.storeEmail.toLowerCase()).catch((error) => console.error(error)))
              .catch((error) => {
                var errorMessage = error.message;
                if (errorMessage.includes('another')) {
                  errorMessage = "Ya existe una tienda con este email. Por favor inicie sesión."
                } else {
                  errorMessage = "Tiene problemas de conexión, vuelva a intentar más tarde."
                }
                setIsLoading(false)
                setLoginError(errorMessage);
              });
          } else {
            setLoginError("Esta tienda no ha sido autorizada por Krece. Para afiliar su tienda por favor comuníquese con nosotros al +58 4149022115.");
            setIsLoading(false)
          }
        });
    } catch (err) {
      setLoginError("Ocuriió un problema, probablemente tenga mala conexión.")
      setIsLoading(false)
    }
  }

  const handleLogin = async () => {
    if (await completePendingGoogleAuth()) return;

    try {
      if (!loginData.storeEmail || !loginData.password) {
        setLoginError('Por favor, rellene todas las casillas.');
        return;
      }

      setLoginError('');
      setIsLoading(true)

      firestore.collection('Stores').doc(loginData.storeEmail.toLowerCase()).get()
        .then((docSnapshot) => {
          if (docSnapshot.exists) {
            auth.signInWithEmailAndPassword(loginData.storeEmail.toLowerCase(), loginData.password)
              .catch((error) => {
                const errorMessage = error.message;
                if (errorMessage.includes('network')) {
                  setLoginError("Mala conexión: Por favor chequee su conexión a internet.")
                } else if (errorMessage.includes('auth/visibility-check-was-unavailable')) {
                  setFirebaseDown(true);
                } else {
                  setLoginError("Email o contraseña incorrecta.")
                }
                setIsLoading(false)
              });
          } else {
            setLoginError("Esta tienda no tiene cuenta con Krece, por favor cree una cuenta.");
            setIsLoading(false)
          }
        }).catch(() => {
          setLoginError("Ocurrió un problema, probablemente tenga mala conexión.")
          setIsLoading(false)
        });
    } catch (err) {
      setLoginError("Ocurrió un problema, probablemente tenga mala conexión.")
      setIsLoading(false)
    }
  };

  const handleGoogleSignIn = async () => {
    if (enable_2fa && !twoFactorSuccess) return;

    setLoginError('');
    setInfoMessage('');
    setPendingGoogleAuth(null);
    setIsGoogleLoading(true);

    const result = await startGoogleSignIn();
    if (result.success) return;
    if ('pending' in result) {
      setPendingGoogleFlow(result.pending);
    } else {
      if (result.firebaseDown) setFirebaseDown(true);
      setLoginError(result.error);
    }
    setIsGoogleLoading(false);
  };

  const handleResetPassword = () => {
    if (!loginData.storeEmail) {
      setLoginError("Por favor, rellene todas las casillas");
      return;
    }

    setLoginError('')
    setIsLoading(true)

    try {
      auth.sendPasswordResetEmail(loginData.storeEmail.toLowerCase()).then(function () {
        setInfoMessage('Le enviamos un email para resetear su contraseña.');
      }).catch(function (error) {
        setLoginError("No existe una cuenta con este email.");
      });
    } catch (err) {
      setLoginError("Ocurrió un problema, probablemente tenga mala conexión.");
    } finally {
      setIsLoading(false)
    }
  }

  const goToPage = (x: number) => {
    setInfoMessage('')
    setLoginError('')
    setPendingGoogleAuth(null);
    setLoginData({
      storeEmail: '',
      password: '',
      password2: ''
    });
    setPage(x);
  }

  const submitLabel = pendingGoogleAuth
    ? pendingGoogleAuth.action === 'create_password'
      ? 'Crear contraseña y conectar Google'
      : 'Conectar Google'
    : page === 1 ? "Ingresar" : page === 2 ? "Enviar" : "Registrarse";
  const submitButtonClassName = page === 1 && !pendingGoogleAuth
    ? "w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium border border-neutral-800 bg-neutral-950 text-neutral-100 hover:bg-neutral-900 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 focus:ring-offset-neutral-900 transition-colors"
    : "w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 focus:ring-offset-neutral-900 transition-colors";

  return (
    <>
      {firebaseDown ? (
        <div className="m-auto flex min-h-full min-w-full flex-col items-center justify-center" id="firebase-outage">
          <div className="w-full max-w-md px-6 py-8">
            <div className="mb-8 flex justify-center">
              <Logo className="w-48 h-auto" fill="fill-primary-500" />
            </div>

            <div className="bg-neutral-950/50 md:bg-background rounded-xl border border-neutral-800 shadow-xl px-8 py-10 text-center">
              <div className="mb-6">
                <div className="w-16 h-16 mx-auto mb-4 bg-amber-500/20 rounded-full flex items-center justify-center">
                 <AlertCircle className="size-8 text-amber-500" />
                </div>
                <h2 className="text-2xl font-semibold text-neutral-100 tracking-tighter mb-2">
                  Servicio Temporalmente No Disponible
                </h2>
                <p className="text-neutral-400 mb-6">
                  Estamos experimentando problemas técnicos temporales. Nuestro equipo está trabajando para restablecer el servicio lo antes posible.
                </p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-center space-x-2 text-sm text-neutral-500">
                  <div className="w-2 h-2 bg-amber-500 rounded-full animate-pulse"></div>
                  <span>Intentando reconectar...</span>
                </div>
                
                <Button
                  onClick={() => {
                    setFirebaseDown(false);
                    setLoginError('');
                    setIsLoading(false);
                    setIsGoogleLoading(false);
                  }}
                  className="w-full flex justify-center py-3 px-4 rounded-lg text-sm font-medium text-white bg-primary-600 hover:bg-primary-500 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 focus:ring-offset-neutral-900 transition-colors"
                >
                  Intentar Nuevamente
                </Button>
              </div>
            </div>
          </div>
        </div>
      ) : (
		<div className={`m-auto flex min-h-full min-w-full flex-col items-center ${!enable_2fa ? 'justify-center' : 'justify-start'}`} id="login">
      <div className="w-full max-w-md px-6 py-8">
        <div className="mb-8 flex justify-center">
          <Logo className="w-48 h-auto" fill="fill-primary-500" />
        </div>

        <div className="bg-neutral-950/50 md:bg-background rounded-xl border border-neutral-800 shadow-xl px-8 py-10">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-semibold text-neutral-100 tracking-tighter">
              {page === 1 && "Iniciar Sesión"}
              {page === 2 && "Recuperar Contraseña"}
              {page === 3 && (pendingGoogleAuth ? "Crear Contraseña" : "Registrarse")}
            </h2>
            <p className="text-neutral-400">
              {page === 1 && "Ingresa tus credenciales para continuar"}
              {page === 2 && "Te enviaremos un correo para restablecer tu contraseña"}
              {page === 3 && (pendingGoogleAuth ? "Crea una contraseña para conectar Google" : "Crea una nueva cuenta para comenzar")}
            </p>
          </div>

          <form className="space-y-4" onSubmit={(e) => {
            e.preventDefault();
            if (page === 1 && enable_2fa && !twoFactorSuccess && !pendingGoogleAuth) return;

            page === 1 ? handleLogin() : page === 2 ? handleResetPassword() : handleSignUp()
          }}>
            {page === 1 && !pendingGoogleAuth && (
              <>
                <Button
                  type="button"
                  onClick={handleGoogleSignIn}
                  disabled={(enable_2fa && !twoFactorSuccess) || isLoading}
                  loading={isGoogleLoading}
                  className="w-full border border-neutral-0 bg-neutral-0 py-3 text-neutral-1000 hover:bg-neutral-100"
                  containerClassName="w-full justify-center"
                  Icon={<span className="flex size-5 items-center justify-center rounded-full bg-neutral-1000 text-xs font-bold text-neutral-0">G</span>}
                >
                  Continuar con Google
                </Button>
                <div className="flex items-center gap-3 py-1">
                  <div className="h-px flex-1 bg-neutral-800" />
                  <span className="text-xs text-neutral-500">o ingresa con email</span>
                  <div className="h-px flex-1 bg-neutral-800" />
                </div>
              </>
            )}

            <Input
              Icon={<Mail className="size-4 text-neutral-300" />}
              name="storeEmail"
              type="email"
              value={loginData.storeEmail}
              onChange={(value) => handleLoginInputChange({ target: { name: 'storeEmail', value } })}
              placeholder="nombre@empresa.com"
              floatingLabel="Email de la tienda"
              disabled={!!pendingGoogleAuth}
            />

            {(page === 1 || page === 3) && (
              <Input
                Icon={<Lock className="size-4 text-neutral-300" />}
                name="password"
                type="password"
                value={loginData.password}
                onChange={(value) => handleLoginInputChange({ target: { name: 'password', value } })}
                placeholder="••••••••"
                floatingLabel="Contraseña"
              />
            )}

            {page === 3 && (
              <Input
                Icon={<Lock className="size-4 text-neutral-300" />}
                name="password2"
                type="password"
                value={loginData.password2}
                onChange={(value) => handleLoginInputChange({ target: { name: 'password2', value } })}
                placeholder="••••••••"
                floatingLabel="Repetir contraseña"
              />
            )}

            {page === 1 && enable_2fa && !pendingGoogleAuth && <TwoFactorAuth select_phone_numbers={dev_2fa_numbers} country={'rd'} onSuccess={() => setTwoFactorSuccess(true)} />}

            {loginError && (
              <Notice message={loginError} variant="error" />
            )}

            {infoMessage && (
              <Notice message={infoMessage} variant={pendingGoogleAuth ? "info" : "success"} />
            )}

            <Button
              disabled={(page === 1 && enable_2fa && !twoFactorSuccess && !pendingGoogleAuth) || isGoogleLoading}
              type="submit"
              loading={isLoading}
              className={submitButtonClassName}
            >
              {submitLabel}
            </Button>


            {page === 1 ? (
              <div className="mt-8 flex flex-col space-y-3">
                <button
                  type="button"
                  onClick={() => goToPage(3)}
                  className="text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  ¿Cuenta nueva? <span className="text-primary-400">Crear contraseña</span>
                </button>

                <button
                  type="button"
                  onClick={() => goToPage(2)}
                  className="text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  ¿Olvidaste tu contraseña? <span className="text-primary-400">Recupérala</span>
                </button>
              </div>
            ) : (
              <div className="mt-8 flex justify-center">
                <button
                  type="button"
                  onClick={() => goToPage(1)}
                  className="flex items-center gap-2 text-sm text-neutral-400 hover:text-neutral-200 transition-colors"
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M9.707 16.707a1 1 0 01-1.414 0l-6-6a1 1 0 010-1.414l6-6a1 1 0 011.414 1.414L5.414 9H17a1 1 0 110 2H5.414l4.293 4.293a1 1 0 010 1.414z" clipRule="evenodd" />
                  </svg>
                  {page === 2 ? "Volver a inicio de sesión" : "Ya tengo una cuenta"}
                </button>
              </div>
            )}
          </form>
        </div>
      </div>
    </div>
      )}
    </>
  )
}

export default LoginForm;
